/**
 * Use this file for JavaScript code that you want to run in the front-end
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any
 * JavaScript running in the front-end, then you should delete this file and remove
 * the `viewScript` property from `block.json`.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

function ensureYoutubeAPIReady() {
	return new Promise((resolve, reject) => {
		if (window.YT && window.YT.Player) {
			return resolve();
		}

		let otherYoutubeReadyCb = null;
		if (typeof window.onYouTubeIframeAPIReady !== "undefined") {
			otherYoutubeReadyCb = window.onYouTubeIframeAPIReady;
		}

		window.onYouTubeIframeAPIReady = () => {
			otherYoutubeReadyCb && otherYoutubeReadyCb();
			resolve();
		};

		let script = document.createElement("script");
		script.id = "youtube-iframe-script";
		script.src = "https://www.youtube.com/iframe_api";
		let firstScriptTag = document.getElementsByTagName("script")[0];
		firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
	});
}

function ensureVimeoAPIReady() {
	return new Promise((resolve, reject) => {
		if (window.Vimeo && window.Vimeo.Player) {
			return resolve();
		}

		let script = document.createElement("script");
		script.id = "vimeo-iframe-script";
		script.src = "https://player.vimeo.com/api/player.js";

		// Set up onload event handler
		script.onload = () => {
			// Check if the necessary objects are now available
			if (window.Vimeo && window.Vimeo.Player) {
				resolve();
			} else {
				reject(
					new Error(
						"Vimeo API script loaded, but Vimeo or Vimeo.Player is still not available",
					),
				);
			}
		};

		// Set up onerror event handler
		script.onerror = () => {
			reject(new Error("Error loading Vimeo API script"));
		};

		let firstScriptTag = document.getElementsByTagName("script")[0];
		firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
	});
}

const removePosterAndOverlay = (video) => {
	const poster = video.querySelector(".media-and-content-video__poster");
	const overlay = video.querySelector(".media-and-content-video__overlay");

	if (poster) {
		poster.style.opacity = 0;
		poster.style.pointerEvents = "none";
	}

	if (overlay) {
		overlay.style.opacity = 0;
		overlay.style.pointerEvents = "none";
	}
};

const enterKeyPressPlay = (video, play) => {
	const playButton = video.querySelector(
		".media-and-content-video__overlay-icon",
	);
	if (playButton) {
		playButton.addEventListener("keydown", (e) => {
			if (e.key === "Enter") {
				removePosterAndOverlay(video);
				play();
			}
		});
	}
};

const handleIframeVideos = () => {
	const videoCollection = document.getElementsByClassName(
		"media-and-content-video__stacking-grid",
	);

	if (videoCollection) {
		const videoArray = Array.from(videoCollection);

		ensureYoutubeAPIReady().then(() => {
			for (let i = 0; i < videoArray.length; i++) {
				const videoItem = videoArray[i];
				// Check if the current videoItem has the class 'source-video-youtube'
				if (videoItem.dataset.videoType === "video-youtube") {
					// If the class is found, run the function and stop the loop
					videoArray.forEach((video, i) => {
						// events if youtube video is loaded
						const frame = video.getElementsByTagName("iframe");
						if (frame.length) {
							if (video.dataset.videoType === "video-youtube") {
								frame[0].style.pointerEvents = "none";

								let playAfterReady = false;
								let isPlayerReady = false;
								const player = new YT.Player(frame[0], {
									events: {
										onReady: () => {
											console.log("ready");
											isPlayerReady = true;
											if (playAfterReady) {
												player.playVideo();
											}
										},
										onStateChange: (e) => {
											if (e.target.getPlayerState() === 1) {
												frame[0].style.visibility = "visible";
												frame[0].style.pointerEvents = "auto";
												removePosterAndOverlay(video);
											}
											if (
												video.dataset.loop === "true" &&
												e.target.getPlayerState() === 0
											) {
												player.playVideo();
											}
										},
									},
								});

								function handlePlay() {
									if (!isPlayerReady) {
										playAfterReady = true;
									} else {
										player.playVideo();
									}
								}

								enterKeyPressPlay(video, handlePlay);
								if (video.dataset.autoplay === "true") {
									handlePlay();
								}
								video.addEventListener("click", () => {
									handlePlay();
								});
							}
						}
					});
					break;
				}
			}
		});

		for (let i = 0; i < videoArray.length; i++) {
			const videoItem = videoArray[i];
			// Check if the current videoItem has the class 'source-video-vimeo'
			if (videoItem.dataset.videoType === "video-vimeo") {
				// If the class is found, run the function and stop the loop
				ensureVimeoAPIReady().then(() => {
					videoArray.forEach((video, i) => {
						// events if Vimeo video is loaded
						const frame = video.getElementsByTagName("iframe");

						if (frame.length) {
							if (video.dataset.videoType === "video-vimeo") {
								frame[0].onload = function () {
									ensureVimeoAPIReady().then(() => {
										const player = new Vimeo.Player(frame[0]);
										const play = () => {
											player.play();
										};

										const poster = video.querySelector(
											".media-and-content-video__poster",
										);
										const overlay = video.querySelector(
											".media-and-content-video__overlay",
										);

										if (poster) {
											poster.addEventListener("click", () => {
												play();
											});
										}

										if (overlay) {
											overlay.addEventListener("click", () => {
												play();
											});
										}

										enterKeyPressPlay(video, play);

										player.on("play", () => {
											removePosterAndOverlay(video);
										});
									});
								};
							}
						}
					});
				});
				break;
			}
		}
	}
};

const handleLocalVideos = () => {
	const fullWidthVideoBlocks = document.querySelectorAll(
		".wp-block-block-builder-by-arc-media-and-content",
	);

	fullWidthVideoBlocks.forEach((block) => {
		const video = block.querySelector(".media-and-content__local-video");
		const playButton = block.querySelector(".media-and-content-video__overlay");
		const stackingGrid = block.querySelector(
			".media-and-content-video__stacking-grid",
		);

		if (video) {
			const play = () => {
				video.play();
			};

			const poster = block.querySelector(".media-and-content-video__poster");
			const overlay = block.querySelector(".media-and-content-video__overlay");

			if (poster) {
				if (stackingGrid.dataset.controls === "true") {
					video.controls = false;
				}

				poster.addEventListener("click", () => {
					if (stackingGrid.dataset.controls === "true") {
						video.controls = true;
					}
					play();
				});
			}

			if (overlay) {
				overlay.addEventListener("click", () => {
					if (stackingGrid.dataset.controls === "true") {
						video.controls = true;
					}
					play();
				});
			}

			if (playButton) {
				playButton.addEventListener("keydown", (e) => {
					if (e.key === "Enter") {
						if (stackingGrid.dataset.controls === "true") {
							video.controls = true;
						}
						removePosterAndOverlay(video);
						play();
					}
				});
			}

			video.addEventListener("playing", function () {
				removePosterAndOverlay(block);
			});
		}
	});
};

// Modal functionality for media-and-content block
const handleMediaAndContentModal = () => {
	const videoThumbnails = document.querySelectorAll('.media-and-content__video-thumbnail');
	
	videoThumbnails.forEach((thumbnail, index) => {
		thumbnail.addEventListener('click', function(e) {
			e.preventDefault();
			
			// Get the block data - try multiple selectors to find the block
			let block = this.closest('.wp-block-block-builder-by-arc-media-and-content');
			if (!block) {
				block = this.closest('.variable-container');
			}
			if (!block) {
				block = this.closest('[data-video-type]');
			}
			
			if (!block) {
				return;
			}
			
			// Get video data from block attributes or data attributes
			const videoType = block.dataset.videoType || 'video-local';
			const videoIframe = block.dataset.videoIframe || '';
			const backgroundVideo = block.dataset.backgroundVideo || '';
			const autoplay = block.dataset.autoplay === 'true';
			const controls = block.dataset.controls !== 'false';
			const loop = block.dataset.loop === 'true';
			
			// Create modal
			createVideoModal(videoType, videoIframe, backgroundVideo, autoplay, controls, loop);
		});
		
		// Add keyboard support
		thumbnail.addEventListener('keydown', function(e) {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				this.click();
			}
		});
		
		// Make it focusable
		thumbnail.setAttribute('tabindex', '0');
		thumbnail.setAttribute('role', 'button');
		thumbnail.setAttribute('aria-label', 'Play video');
	});
};

const createVideoModal = (videoType, videoIframe, backgroundVideo, autoplay, controls, loop) => {
	// Remove existing modal if any
	const existingModal = document.querySelector('.video-modal__overlay');
	if (existingModal) {
		existingModal.remove();
	}
	
	// Create modal overlay
	const modalOverlay = document.createElement('div');
	modalOverlay.className = 'video-modal__overlay';
	modalOverlay.style.cssText = `
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(0, 0, 0, 0.9);
		z-index: 9999;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 20px;
		opacity: 0;
		transition: opacity 200ms ease-in-out;
	`;
	
	// Create modal content
	const modalContent = document.createElement('div');
	modalContent.className = 'video-modal__content';
	modalContent.style.cssText = `
		position: relative;
		width: 100%;
		max-width: 1200px;
		max-height: 90vh;
		background-color: #000;
		border-radius: 8px;
		overflow: hidden;
		box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
		outline: none;
		transform: scale(0.9);
		transition: transform 200ms ease-in-out;
	`;
	
	// Create close button
	const closeButton = document.createElement('button');
	closeButton.className = 'video-modal__close';
	closeButton.innerHTML = '×';
	closeButton.style.cssText = `
		position: absolute;
		top: 10px;
		right: 10px;
		background: rgba(0, 0, 0, 0.7);
		border: none;
		border-radius: 50%;
		width: 40px;
		height: 40px;
		color: white;
		cursor: pointer;
		z-index: 10;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 18px;
		font-weight: bold;
		transition: background-color 0.2s ease, transform 0.2s ease;
	`;
	
	closeButton.addEventListener('click', () => {
		closeModal(modalOverlay);
	});
	
	// Create video content
	let videoContent = null;
	
	switch (videoType) {
		case 'video-local':
			if (backgroundVideo) {
				videoContent = document.createElement('video');
				videoContent.className = 'video-modal__video';
				videoContent.style.cssText = `
					width: 100%;
					height: 100%;
					max-height: 80vh;
					border-radius: 8px;
				`;
				videoContent.autoplay = autoplay;
				videoContent.muted = autoplay;
				videoContent.loop = loop;
				videoContent.controls = controls;
				
				const source = document.createElement('source');
				source.src = backgroundVideo;
				source.type = 'video/mp4';
				videoContent.appendChild(source);
				videoContent.appendChild(document.createTextNode('Your browser does not support this video.'));
			}
			break;
			
		case 'video-youtube':
			if (videoIframe) {
				videoContent = document.createElement('iframe');
				videoContent.className = 'video-modal__iframe';
				videoContent.style.cssText = `
					border-radius: 8px;
					min-height: 50vh;
					aspect-ratio: 16/9;
					width: 100%;
					height: 100%;
				`;
				videoContent.src = `${videoIframe}${autoplay ? "&autoplay=1&mute=1" : ""}${controls ? "&controls=1" : "&controls=0"}&enablejsapi=1`;
				videoContent.title = 'YouTube video player';
				videoContent.frameBorder = '0';
				videoContent.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
				videoContent.allowFullScreen = true;
			}
			break;
			
		case 'video-vimeo':
			if (videoIframe) {
				videoContent = document.createElement('iframe');
				videoContent.className = 'video-modal__iframe';
				videoContent.style.cssText = `
					border-radius: 8px;
					min-height: 50vh;
					aspect-ratio: 16/9;
					width: 100%;
					height: 100%;
				`;
				videoContent.src = `${videoIframe}?${autoplay ? "&autoplay=1&muted=1" : ""}${controls ? "&controls=1" : "&controls=0"}${loop ? "&loop=1" : "&loop=0"}`;
				videoContent.frameBorder = '0';
				videoContent.allow = 'autoplay; fullscreen; picture-in-picture';
				videoContent.allowFullScreen = true;
			}
			break;
	}
	
	if (videoContent) {
		modalContent.appendChild(closeButton);
		modalContent.appendChild(videoContent);
		modalOverlay.appendChild(modalContent);
		document.body.appendChild(modalOverlay);
		
		// Animate modal in
		setTimeout(() => {
			modalOverlay.style.opacity = '1';
			modalContent.style.transform = 'scale(1)';
		}, 10);
		
		// Close on overlay click
		modalOverlay.addEventListener('click', (e) => {
			if (e.target === modalOverlay) {
				closeModal(modalOverlay);
			}
		});
		
		// Close on escape key
		document.addEventListener('keydown', function escapeHandler(e) {
			if (e.key === 'Escape') {
				closeModal(modalOverlay);
				document.removeEventListener('keydown', escapeHandler);
			}
		});
	}
};

const closeModal = (modalOverlay) => {
	modalOverlay.style.opacity = '0';
	const modalContent = modalOverlay.querySelector('.video-modal__content');
	if (modalContent) {
		modalContent.style.transform = 'scale(0.9)';
	}
	
	setTimeout(() => {
		if (modalOverlay.parentNode) {
			modalOverlay.parentNode.removeChild(modalOverlay);
		}
	}, 200);
};

if (document.readyState !== "loading") {
	handleIframeVideos();
	handleLocalVideos();
	handleMediaAndContentModal();
} else {
	document.addEventListener("DOMContentLoaded", handleIframeVideos);
	document.addEventListener("DOMContentLoaded", handleLocalVideos);
	document.addEventListener("DOMContentLoaded", handleMediaAndContentModal);
}
